import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { ArrowLeft, BookOpen, ExternalLink, Loader2 } from "lucide-react";
import { useToolDetail } from "../../application/useMarketplace";
import { centerState } from "../../domain/types";
import AdoptButton from "../components/AdoptButton";
import CenterManagedNotice from "../components/CenterManagedNotice";
import ToolLabels from "../components/ToolLabels";
import { toolDescription, toolExternalLinks, toolName } from "../toolCopy";
import { toolPriceLabel } from "../price";
import { useBillingPeriod } from "../../../subscription";

const ToolDetailPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { slug = "" } = useParams();
    const { data: tool, isLoading, error } = useToolDetail(slug);
    // The period this account is billed in: what adopting would cost it.
    const period = useBillingPeriod();

    return (
        <div className="mx-auto max-w-3xl px-4 py-6">
            <Link
                to="/tools-catalog"
                className="mb-6 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-brand-600 dark:text-gray-400"
            >
                <ArrowLeft size={16} />
                {t("marketplace.backToList")}
            </Link>

            {isLoading && (
                <div className="flex items-center justify-center py-16 text-content-tertiary">
                    <Loader2 className="animate-spin" size={28} />
                </div>
            )}

            {error && !isLoading && (
                <div className="rounded-xl border border-error-200 bg-error-50 p-6 text-center text-sm text-error-600 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
                    {t("marketplace.errorNotFound")}
                </div>
            )}

            {!isLoading && !error && tool && (
                <article className="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-sm dark:border-gray-800 dark:bg-gray-900">
                    <div className="flex items-start justify-between gap-3">
                        <h1 className="flex flex-wrap items-center gap-2 text-2xl font-semibold text-content-primary">
                            {toolName(t, tool)}
                        </h1>
                        <span className="shrink-0 rounded-full bg-brand-50 px-3 py-1 text-sm font-medium text-brand-600 dark:bg-brand-500/10 dark:text-brand-400">
                            {toolPriceLabel(t, tool, i18n.language || "es", period)}
                        </span>
                    </div>

                    <ToolLabels tool={tool} />

                    <p className="mt-5 whitespace-pre-line text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                        {toolDescription(t, tool)}
                    </p>

                    {/* Adopting is the only action here. A tool's own workspace
                        is reached from the navigation, which is where someone
                        looks for it after the first time. */}
                    {(tool.disabled_by_center || tool.managed_by_center) && (
                        <CenterManagedNotice state={centerState(tool)} />
                    )}

                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        <AdoptButton tool={tool} />
                    </div>

                    <ToolExternalLinks tool={tool} language={i18n.language} />
                </article>
            )}
        </div>
    );
};

/**
 * Where the tool is explained at length.
 *
 * The catalogue entry says what a tool is for; what it costs in detail, and
 * how to set it up, live on the public site. Linked out rather than duplicated
 * here, because the marketing page and the setup guide are maintained there
 * and would drift the moment we copied them.
 */
const ToolExternalLinks: React.FC<{
    tool: Parameters<typeof toolExternalLinks>[0];
    language: string | undefined;
}> = ({ tool, language }) => {
    const { t } = useTranslation();
    const links = toolExternalLinks(tool, language);
    if (!links) return null;

    const linkClass =
        "inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:underline dark:text-brand-400";

    return (
        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-gray-100 pt-5 dark:border-gray-800">
            <a href={links.moreInfo} target="_blank" rel="noreferrer" className={linkClass}>
                <ExternalLink size={16} aria-hidden="true" />
                {t("marketplace.moreInfo")}
            </a>
            <a href={links.documentation} target="_blank" rel="noreferrer" className={linkClass}>
                <BookOpen size={16} aria-hidden="true" />
                {t("marketplace.documentation")}
            </a>
        </div>
    );
};

export default ToolDetailPage;
